import React, { useEffect, useState, useRef } from 'react';
import { LinearProgress } from '@mui/material';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { jwtDecode } from 'jwt-decode';
import { ACCESS_TOKEN_NAME } from '../../utils/enums';
import { loginRequest, getAuthority } from '../../authConfig';
import { logAppError, logAuthStep } from '../../utils/logActions';
import configData from '../../config.json';
import { Sidebar } from '../../Components/Layout/components';
import { ErrorOutlineOutlined } from '@mui/icons-material';
import AlternateMessageDialog from '../../Components/common/AlternateMessageDialogComponent/AlternateMessageDialog';
import SetPasswordCard from './components/SetPasswordCard';
import { resetPasswordSetup, validateDirectInvite } from '../../actions/passwordSetup';

const PASSWORD_SETUP_TYPE = 'changePassword';

export default function InteractionPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { instance, inProgress, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [error, setError] = useState(null);
  const [loadingStep, setLoadingStep] = useState('Initializing...');
  // Email of the user while the set-password step is shown
  const [passwordSetupEmail, setPasswordSetupEmail] = useState(null);
  const processing = useRef(false);
  const passwordSetupStarted = useRef(false);
  const urlHint = searchParams.get('id_token_hint');
  const urlPage = searchParams.get('page');
  const urlPageId = searchParams.get('id');
  const urlType = searchParams.get('type');
  const urlJobId = searchParams.get('jobId');
  const storedHint = localStorage.getItem('active_id_token_hint');
  const idTokenHint = urlHint || storedHint;

  const cleanup = () => {
    localStorage.removeItem('active_id_token_hint');
    localStorage.removeItem('page');
    localStorage.removeItem('pageId');
    localStorage.removeItem('type');
    localStorage.removeItem('jobId');
  };

  const finishPasswordSetup = () => {
    cleanup();
    dispatch(resetPasswordSetup());
    navigate('/dashboard');
  };

  const getLinkEmail = (hint) => {
    try {
      return jwtDecode(hint).email;
    } catch {
      return null;
    }
  };

  // Set-password links skip validate-magic-link: validate-direct-invite-magic-link decides whether to show the step.
  // The link data stays in localStorage until the password is set, so a refresh lands here and asks again.
  const startPasswordSetup = async (email) => {
    passwordSetupStarted.current = true;
    setLoadingStep('Checking your account...');
    try {
      const { value: shouldShow } = await dispatch(validateDirectInvite(email));
      logAuthStep('PasswordSetup_Validate_Result', { shouldShow });
      if (!shouldShow) return finishPasswordSetup();
      return setPasswordSetupEmail(email);
    } catch (err) {
      logAppError('PasswordSetup_Validate_Error', { error: err?.message });
      return finishPasswordSetup();
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    const handleJourney = async () => {
      if (processing.current || passwordSetupStarted.current || inProgress !== 'none') return;

      if (urlHint) localStorage.setItem('active_id_token_hint', urlHint);
      if (urlPage) localStorage.setItem('page', urlPage);
      if (urlPageId) localStorage.setItem('pageId', urlPageId);
      if (urlType) localStorage.setItem('type', urlType);
      if (urlJobId) localStorage.setItem('jobId', urlJobId);

      const idTokenHint = localStorage.getItem('active_id_token_hint');
      const page = localStorage.getItem('page');
      const pageId = localStorage.getItem('pageId');
      const type = localStorage.getItem('type');
      const jobId = localStorage.getItem('jobId');
      const isPasswordSetup = type === PASSWORD_SETUP_TYPE;
      const accountEmail = accounts[0]?.idTokenClaims?.email;

      if (!isAuthenticated) {
        if (!idTokenHint) {
          setError('No valid magic link provided.');
          logAppError('MagicLink_No_Token', { message: 'No valid magic link provided.' });
          return;
        }
        processing.current = true;
        setLoadingStep('Redirecting to secure login...');
        instance.loginRedirect({
          ...loginRequest,
          authority: getAuthority(idTokenHint),
          extraQueryParameters: { id_token_hint: idTokenHint },
          redirectUri: `${window.location.origin}/interaction`
        });
        return;
      }

      if (isAuthenticated && idTokenHint && isPasswordSetup) {
        await startPasswordSetup(getLinkEmail(idTokenHint) || accountEmail);
        return;
      }

      if (isAuthenticated && idTokenHint) {
        processing.current = true;
        setLoadingStep('Validating link security...');
        try {
          const token = localStorage.getItem(ACCESS_TOKEN_NAME);
          const decoded = jwtDecode(idTokenHint);
          const response = await fetch(`${configData.REACT_APP_BACKEND_URL}/api/v1/validate-magic-link`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`
            },
            body: JSON.stringify({
              Jti: decoded.jti,
              Email: decoded.email,
              ExpiryDateTime: new Date(decoded.exp * 1000).toISOString()
            })
          });

          logAuthStep('MagicLink_Validation', { jti: decoded.jti, email: decoded.email, exp: decoded.exp, status: response.status });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Server Returned Validation Error: ${errorText}`);
          }

          const result = await response.json();

          if (result?.Data?.Message === 'Success!') {
            setLoadingStep('Success! Redirecting you...');

            if (page && pageId) {
              const target =
                type === 'approve' ? `/dashboard/${page}/${pageId}?approver=true` : `/dashboard/${page}/${pageId}`;
              cleanup();
              return navigate(target);
            }

            if (jobId) {
              cleanup();
              return navigate(`/jobs/${jobId}?action=apply`);
            }

            cleanup();
            return navigate('/dashboard');
          } else {
            setError(result?.Data?.Message || 'Magic link validation failed.');
            localStorage.removeItem('active_id_token_hint');
            logAppError('MagicLink_Validation_Failed', {  error: result?.Data?.Message });
            setTimeout(() => {
              instance.logoutRedirect({
                  postLogoutRedirectUri: '/',
              }).catch((e) => {
                  console.error(e);
              });
            }, 100);
          }
        } catch (err) {
          instance.logoutRedirect({
              postLogoutRedirectUri: '/',
          }).catch((e) => {
            console.error(e);
          });
          console.error('Validation Error:', err);
          logAppError('MagicLink_Validation_Error', { error: err.message });
          setError('Validation service is currently unavailable.');
        } finally {
          processing.current = false;
        }
      }
    };
    handleJourney();

    return () => {
      controller.abort();
    }
  }, [isAuthenticated, inProgress]);

  if (passwordSetupEmail) {
    return <SetPasswordCard email={passwordSetupEmail} onSuccess={finishPasswordSetup} />;
  }

  if (error) {
    return (
      <div className="h-full">
        <Sidebar showMenu={false} />
        <AlternateMessageDialog
          icon={<ErrorOutlineOutlined />}
          headerLabel="Access Denied"
          startTracking="This secure link is no longer valid. You will be logged out automatically."
          mainButtonLabel="Go back to Home"
          mainButtonEvent={() => navigate('/')}
          isApprover={true}
          headerClasses="text-2xl"
          containerClasses="mt-25 [&>p>svg]:!text-4xl"
          mainContainerClasses="!h-screen"
        />
      </div>
    );
  }

  return (
    <div className="h-full">
      <Sidebar showMenu={false} />
      <AlternateMessageDialog
        headerLabel={loadingStep}
        startTracking={<LinearProgress sx={{ height: '10px', borderRadius: '5px', mx: '25%', width: '50%' }} />}
        isApprover={true}
        headerClasses="text-2xl"
        containerClasses="mt-25 [&>p>svg]:!text-4xl"
        mainContainerClasses="!h-screen"
      />
    </div>
  );
}
